import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '../ui/LanguageSwitcher'

function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  const handleNavClick = (e, href) => {
    // Check if this is a hash link on the home page
    if (href.startsWith('/#')) {
      const elementId = href.substring(2)

      if (location.pathname === '/') {
        // Already on home page, just scroll to the element
        e.preventDefault()
        setIsMenuOpen(false)
        const element = document.getElementById(elementId)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      } else {
        // Navigate to home page first, then scroll
        e.preventDefault()
        setIsMenuOpen(false)
        navigate('/')
        setTimeout(() => {
          const element = document.getElementById(elementId)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        }, 100)
      }
    } else {
      setIsMenuOpen(false)
    }
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b border-black/10">
      <nav className="flex items-center justify-between h-[56px] px-20 max-w-lg mx-auto">
        {/* Logo */}
        <Link
          to="/"
          className="text-lato-bold text-[28px] hover:opacity-70 transition-opacity z-50"
        >
          {t('navigation.logo')}
        </Link>

        {/* Desktop Navigation Links */}
        <div className="flex items-center gap-10">
          <div className="hidden tablet:flex items-center gap-24">
            {t('navigation.links', { returnObjects: true }).map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-lato-regular text-base desktop:text-lg hover:opacity-70 transition-opacity"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language Switcher - Always visible */}
          <div className="z-50">
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <button
            className="tablet:hidden p-8 z-50 relative"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            <div className="w-24 h-20 relative flex flex-col justify-center items-center">
              <span
                className={`block absolute h-[2px] w-24 bg-black transform transition-all duration-300 ease-in-out ${
                  isMenuOpen ? 'rotate-45' : '-translate-y-[6px]'
                }`}
              />
              <span
                className={`block absolute h-[2px] w-24 bg-black transform transition-all duration-300 ease-in-out ${
                  isMenuOpen ? 'opacity-0' : 'opacity-100'
                }`}
              />
              <span
                className={`block absolute h-[2px] w-24 bg-black transform transition-all duration-300 ease-in-out ${
                  isMenuOpen ? '-rotate-45' : 'translate-y-[6px]'
                }`}
              />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-white z-40 tablet:hidden transform transition-transform duration-300 ease-in-out ${
          isMenuOpen ? 'translate-y-0' : '-translate-y-full'
        }`}
        style={{ top: '56px' }}
      >
        <div className="flex flex-col items-center justify-center h-full gap-40 pb-40">
          {t('navigation.links', { returnObjects: true }).map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-lato-regular text-2xl hover:opacity-70 transition-opacity"
            >
              {link.label}
            </Link>
          ))}

          {/* CTA Button */}
          <a
            href={t('navigation.ctaButton.href')}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-lg px-40"
            onClick={() => setIsMenuOpen(false)}
          >
            {t('navigation.ctaButton.text')}
          </a>
        </div>
      </div>
    </header>
  )
}

export default Header
